/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { extractRelativePath } from "components/Shared/DomainSplit";
import { DGAButton } from "components/Shared/dga-button/DGAButton";
import axios from "axios";
import Image from "next/image";
import { showErrorToast, showSuccessToast } from "lib/toast";
import { FormEvent, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";

import { VotingOptions } from "./Components/VotingOptions";
import {
  fetchVotingPollCounts,
  formatVotingSectionDate,
  submitVote,
  VOTING_LAYOUT,
  VotingPageMetaData,
  VotingSectionFields,
} from "./Helpers/VotingSectionHelpers";

type VotingSectionProps = {
  SectionId: string;
  fields: VotingSectionFields;
  locale: string;
  pageMetaData?: VotingPageMetaData;
  pollCounts?: Record<string, number>;
};

const VotingSection = (props: VotingSectionProps): JSX.Element | null => {
  const { fields, locale, SectionId, pageMetaData, pollCounts } = props;
  const votingOptions = fields?.VotingOptions ?? [];
  const layoutKey = fields?.VotingSectionLayout?.fields?.Title?.value || VOTING_LAYOUT.IMAGE_CARDS;
  const isPoolButtonsLayout = layoutKey === VOTING_LAYOUT.POOL_BUTTONS;

  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
  const [pollCountsState, setPollCountsState] = useState<Record<string, number>>(pollCounts ?? {});
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const sitekey = process.env.NEXT_PUBLIC_GOOGLE_CAPTCHA_API_KEY || "";

  useEffect(() => {
    setPollCountsState(pollCounts ?? {});
  }, [pollCounts]);

  const successToastTitle =
    fields?.SuccessToastTitle?.value?.trim() ||
    (locale === "ar" ? "تم إرسال تصويتك بنجاح." : "Your vote was submitted successfully.");
  const successToastDescription = fields?.SuccessToastDescription?.value?.trim();
  const errorToastTitle =
    fields?.ErrorToastTitle?.value?.trim() ||
    (locale === "ar" ? "حدث خطأ ما. يرجى المحاولة مرة أخرى." : "Something went wrong. Please try again.");
  const errorToastDescription = fields?.ErrorToastDescription?.value?.trim();

  const cmsSectionBg = fields?.SectionBackgroundColor?.value?.trim();
  const formattedDate = formatVotingSectionDate(fields?.Date?.value, locale);

  const hasHeaderContent =
    fields?.Title?.value ||
    fields?.SubTitle?.value ||
    fields?.Description?.value ||
    formattedDate;

  if (!hasHeaderContent && !votingOptions.length) {
    return null;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCaptchaError(false);

    if (!selectedOptionIds.length) {
      return;
    }

    if (!captcha) {
      setCaptchaError(true);
      return;
    }

    const pageName =
      pageMetaData?.pageTitle?.value ||
      pageMetaData?.itemPath?.split("/")?.filter(Boolean)?.pop() ||
      "";

    try {
      setIsLoading(true);
      await submitVote({
        pageId: pageMetaData?.itemId,
        pageName,
        voteIds: selectedOptionIds,
        language: locale,
        captchaResponse: captcha,
      });

      showSuccessToast({
        title: successToastTitle,
        ...(successToastDescription ? { description: successToastDescription } : {}),
      });
      setSelectedOptionIds([]);
      setCaptcha(null);
      recaptchaRef.current?.reset();

      if (isPoolButtonsLayout) {
        const optionIds = votingOptions.map((option) => option.id).filter(Boolean);
        if (optionIds.length) {
          const updatedCounts = await fetchVotingPollCounts(optionIds, pollCountsState);
          setPollCountsState(updatedCounts);
        }
      }
    } catch (error) {
      setCaptcha(null);
      recaptchaRef.current?.reset();

      if (axios.isAxiosError(error)) {
        console.error("Vote submit error:", error.response?.data || error.message);
      }

      showErrorToast({
        title: errorToastTitle,
        ...(errorToastDescription ? { description: errorToastDescription } : {}),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      id={SectionId}
      className={`py-10 ${locale === "ar" ? "rtl" : ""}`}
      style={cmsSectionBg ? { backgroundColor: cmsSectionBg } : undefined}
    >
      <form onSubmit={handleSubmit} className="responsive flex flex-col gap-6">
        {hasHeaderContent && (
          <div className="flex flex-col gap-6">
            {fields?.Title?.value && (
              <h2 className="text-[30px] font-bold leading-[38px] text-primary-1000">
                {fields.Title.value}
              </h2>
            )}

            {(fields?.SubTitle?.value || fields?.Description?.value || formattedDate) && (
              <div className="flex flex-col gap-1">
                {fields?.SubTitle?.value && (
                  <p className="text-[21px] font-semibold text-primary-1000">{fields.SubTitle.value}</p>
                )}
                {fields?.Description?.value && (
                  <p className="text-[15px] leading-[23px] text-primary-1000">
                    {fields.Description.value}
                  </p>
                )}
                {formattedDate && (
                  <div className="flex flex-wrap items-center gap-1 text-[15px] text-neutral-500">
                    {fields?.CalendarIcon?.value?.src && (
                      <Image
                        src={extractRelativePath(fields.CalendarIcon.value.src)}
                        alt={fields.CalendarIcon.value.alt || ""}
                        width={14}
                        height={14}
                        className="shrink-0"
                      />
                    )}
                    <span>{formattedDate.datePart}</span>
                    <span aria-hidden="true">.</span>
                    <span className="!ltr">{formattedDate.timePart}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {votingOptions.length > 0 && (
          <VotingOptions
            layout={layoutKey}
            options={votingOptions}
            selectedOptionIds={selectedOptionIds}
            onToggleOption={(optionId) =>
              setSelectedOptionIds((prev) =>
                prev.includes(optionId)
                  ? prev.filter((id) => id !== optionId)
                  : [...prev, optionId]
              )
            }
            onSelectOption={(optionId) => setSelectedOptionIds([optionId])}
            sectionId={SectionId}
            votingOptionsTitle={fields?.VotingOptionsTitle?.value}
            pollCounts={pollCountsState}
          />
        )}

        <div
          className={`flex w-full max-w-[388px] items-start ${captchaError ? "rounded border-2 border-red-500 p-1" : ""
            }`}
        >
          {sitekey && (
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={sitekey}
              onChange={(value) => {
                setCaptcha(value);
                if (value) setCaptchaError(false);
              }}
              hl={locale}
            />
          )}
        </div>

        {fields?.SubmitButtonText?.value && (
          <DGAButton
            type="submit"
            buttonContent={fields.SubmitButtonText.value}
            disabled={isLoading || !selectedOptionIds.length || !captcha}
            size="lg"
            variant="primary"
          />
        )}
      </form>
    </section>
  );
};

export default VotingSection;
export type { VotingSectionCo, VotingSectionFields } from "./Helpers/VotingSectionHelpers";
